import { addMinutes, format } from 'date-fns';
import { settingsService } from '../../../services/settingsService';
import type { CalendarEvent } from '../../../types/api';
import type { MeetingFormData } from '../types';
import {
  DEFAULT_DURATION,
  DEFAULT_MEETING_TYPE,
  getDefaultCalendarOptionForAccount,
  SCHEDULE_MEETING_NO_COLOR_ID,
} from './constants';

/**
 * Returns the next "30-minute slot" in local time.
 * Examples:
 * - 15:46 -> 16:00
 * - 15:30 -> 16:00 (next slot, not the current boundary)
 */
export const getNext30MinSlotStartDate = (now: Date = new Date()): Date => {
  const startDate = new Date(now);
  startDate.setSeconds(0, 0);

  const minutes = startDate.getMinutes();
  const remainder = minutes % 30;

  const minutesToAdd = remainder === 0 ? 30 : 30 - remainder;
  startDate.setMinutes(minutes + minutesToAdd);

  return startDate;
};

const roundUpToHalfHour = (date: Date): Date => {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);

  const minutes = rounded.getMinutes();
  const remainder = minutes % 30;

  if (remainder !== 0) {
    rounded.setMinutes(minutes + (30 - remainder));
  }

  return rounded;
};

const getEventDateTime = (field?: { dateTime?: string; date?: string }): Date | null => {
  const value = field?.dateTime ?? field?.date;
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

export const getNextAvailableStartDate = (
  events: CalendarEvent[],
  now: Date = new Date(),
): Date => {
  let candidate = getNext30MinSlotStartDate(now);

  const intervals = events
    .map((event) => {
      const start = getEventDateTime(event.start);
      const end = getEventDateTime(event.end);
      return { start, end };
    })
    .filter(
      (interval): interval is { start: Date; end: Date } =>
        !!interval.start && !!interval.end && interval.end > interval.start,
    )
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  for (const interval of intervals) {
    // Ignore meetings that end before or exactly at the candidate.
    if (interval.end <= candidate) {
      continue;
    }

    if (interval.start <= candidate && candidate < interval.end) {
      candidate = roundUpToHalfHour(interval.end);
    }
  }

  return candidate;
};

export const getInitialFormData = (startDateOverride?: Date): Omit<MeetingFormData, 'room'> => {
  const startDate = startDateOverride ?? getNext30MinSlotStartDate(new Date());
  const endDate = addMinutes(startDate, DEFAULT_DURATION);

  return {
    description: '',
    fromDate: format(startDate, 'yyyy-MM-dd'),
    fromTime: format(startDate, 'HH:mm'),
    toDate: format(endDate, 'yyyy-MM-dd'),
    toTime: format(endDate, 'HH:mm'),
    duration: DEFAULT_DURATION,
    selectedColor: SCHEDULE_MEETING_NO_COLOR_ID,
    meetingType: DEFAULT_MEETING_TYPE,
    calendarOption: getDefaultCalendarOptionForAccount(
      Boolean(settingsService.getSettings().calendar.google.connected),
      Boolean(settingsService.getSettings().calendar.microsoft.connected),
    ),
    enableRecording: false,
    enableTranscription: false,
    useBackendApi: true,
  };
};
